import { useState, useEffect } from "react";
import {
  Package, CheckCircle, AlertCircle, ArrowRight, Zap,
  Star, CreditCard, Shield, RefreshCw, XCircle, Users, Plus, Minus, Lock
} from "lucide-react";
import { PLAN_INFO as PLAN_INFO_CONFIG } from "../../config/planConfig";

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_INFO = {
  basic:    { naam: "Basic",    prijs: 59.95, sporters: 10,  kleur: "#6366f1" },
  pro:      { naam: "Pro",      prijs: 109.95, sporters: 40, kleur: "#C9A84C" },
  business: { naam: "Business", prijs: 168.95, sporters: 75, kleur: "#10b981" },
};

const STATUS_INFO = {
  active:          { label: "Actief",               cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  trial:           { label: "Trial",                cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  pending_payment: { label: "Betaling in behandeling", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  suspended:       { label: "Geblokkeerd",          cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  canceled:        { label: "Opgezegd",             cls: "bg-zinc-700/30 text-zinc-400 border-zinc-600/30" },
  none:            { label: "Geen abonnement",      cls: "bg-zinc-800/50 text-zinc-500 border-zinc-700/30" },
};

function StatusBadge({ status }) {
  const s = STATUS_INFO[status] || STATUS_INFO.none;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${s.cls}`}>
      {s.label}
    </span>
  );
}

const PLANS = [
  {
    key: "basic", naam: "Basic", prijs: 59.95, sporters: 10,
    kleur: "#6366f1",
    features: [
      "Maximaal 10 sporters",
      "Schema-builder",
      "Voedingsmodule",
      "Intake formulieren",
      "Oefenenbibliotheek",
    ],
    locked: [],
    highlight: false,
  },
  {
    key: "pro", naam: "Pro", prijs: 109.95, sporters: 40, badge: "Meest gekozen",
    kleur: "#C9A84C",
    features: [
      "Maximaal 40 sporters",
      "Rooster & boekingssysteem",
      "Credits & betaalpakketten",
      "Mappen & templates",
      "Analytics dashboard",
      "Eigen branding & logo",
    ],
    locked: [],
    highlight: true,
  },
  {
    key: "business", naam: "Business", prijs: 168.95, sporters: 75,
    kleur: "#10b981",
    features: [
      "Maximaal 75 sporters",
      "Personeel & beheerders",
      "Facturatie (PDF)",
      "Community feed",
      "Uitgebreide analytics",
      "Prioriteit support",
    ],
    locked: [],
    highlight: false,
  },
];

export default function AbonnementTab({ token, companyInfo }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initiating, setInitiating] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [result, setResult] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [extraSporters, setExtraSporters] = useState(0);
  const [extraSaving, setExtraSaving] = useState(false);
  const [extraResult, setExtraResult] = useState(null);

  // Kortingscode
  const [kortingscode, setKortingscode] = useState("");
  const [kortingValideren, setKortingValideren] = useState(false);
  const [kortingPercentage, setKortingPercentage] = useState(0);
  const [kortingFout, setKortingFout] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/company/subscription`, auth)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setSubscription(d);
        setExtraSporters(d?.extra_sporters || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const valideerKorting = async () => {
    if (!kortingscode.trim()) return;
    setKortingValideren(true); setKortingFout(""); setKortingPercentage(0);
    try {
      const r = await fetch(`${API}/api/discount/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: kortingscode.trim() }),
      });
      let d; try { d = await r.json(); } catch { d = {}; }
      if (!r.ok) throw new Error(d.detail || "Ongeldige kortingscode");
      setKortingPercentage(d.percentage);
    } catch (err) { setKortingFout(err.message || "Ongeldige kortingscode"); }
    finally { setKortingValideren(false); }
  };

  const startPayment = async (planKey) => {
    setInitiating(true);
    setResult(null);
    try {
      const redirectUrl = window.location.origin + "/bedrijf-admin?subscription=success";
      const r = await fetch(`${API}/api/company/subscription/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth.headers },
        body: JSON.stringify({
          plan: planKey,
          bedrijf_naam: companyInfo?.naam || "",
          email: companyInfo?.owner_email || "",
          redirect_url: redirectUrl,
          kortingscode: kortingscode.trim().toUpperCase() || null,
        }),
      });
      const d = await r.json();
      if (r.ok && d.checkout_url) {
        window.location.href = d.checkout_url;
      } else {
        setResult({ ok: false, msg: d.detail || "Betaling starten mislukt" });
      }
    } catch {
      setResult({ ok: false, msg: "Netwerk fout. Probeer opnieuw." });
    }
    setInitiating(false);
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Weet je zeker dat je het abonnement wilt opzeggen?")) return;
    setCanceling(true);
    try {
      await fetch(`${API}/api/company/subscription/cancel`, { method: "POST", ...auth });
      load();
    } catch { /* ignore */ }
    setCanceling(false);
  };

  const handleSaveExtraSporters = async () => {
    setExtraSaving(true);
    setExtraResult(null);
    try {
      const r = await fetch(`${API}/api/company/subscription/extra-sporters`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth.headers },
        body: JSON.stringify({ extra_sporters: extraSporters }),
      });
      const d = await r.json();
      if (r.ok) {
        setExtraResult({ ok: true, msg: "Bijgewerkt!" });
        load();
      } else {
        setExtraResult({ ok: false, msg: d.detail || "Opslaan mislukt" });
      }
    } catch {
      setExtraResult({ ok: false, msg: "Netwerk fout" });
    }
    setExtraSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  const hasActive = subscription?.status === "active" || subscription?.status === "trial";
  const plan = subscription?.plan ? PLAN_INFO[subscription.plan] : null;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Beheer</p>
        <h2 className="text-xl font-bold text-white">Abonnement</h2>
      </div>

      {/* Huidig abonnement */}
      <div className="bg-[#111] border border-white/8 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Huidig abonnement</h3>
          <button onClick={load} className="text-zinc-600 hover:text-zinc-400 transition-colors">
            <RefreshCw size={14} />
          </button>
        </div>

        {!subscription?.plan ? (
          <div className="flex items-center gap-3 py-2">
            <XCircle size={20} className="text-zinc-600" />
            <div>
              <p className="text-zinc-400 font-semibold">Geen actief abonnement</p>
              <p className="text-zinc-600 text-xs mt-0.5">Kies hieronder een pakket om te starten.</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `${PLAN_INFO[subscription.plan]?.kleur}20`, border: `1px solid ${PLAN_INFO[subscription.plan]?.kleur}40` }}
              >
                <Package size={20} style={{ color: PLAN_INFO[subscription.plan]?.kleur }} />
              </div>
              <div>
                <p className="text-white font-bold text-lg">{plan?.naam || subscription.plan?.toUpperCase()}</p>
                <p className="text-zinc-500 text-sm">€{plan?.prijs}/4 weken · max {plan?.sporters} sporters</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={subscription.status} />
              {subscription.override && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-orange-500/15 text-orange-400 border border-orange-500/25">
                  Handmatig geactiveerd
                </span>
              )}
            </div>
          </div>
        )}

        {subscription?.extra_sporters > 0 && (
          <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-sm text-zinc-400">
            <Users size={13} className="text-[#C9A84C]" />
            Extra sporters: <strong className="text-[#C9A84C]">+{subscription.extra_sporters}</strong>
            &nbsp;·&nbsp; totale capaciteit:&nbsp;
            <strong className="text-white">
              {(subscription.sporters_limit || 0) + subscription.extra_sporters} sporters
            </strong>
          </div>
        )}

        {result && (
          <div className={`mt-4 p-3 rounded-xl text-sm ${result.ok ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
            <div className="flex items-center gap-2">
              {result.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
              {result.msg}
            </div>
          </div>
        )}

        {/* Hervatten knop bij openstaande betaling */}
        {subscription?.status === "pending_payment" && subscription?.checkout_url && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <p className="text-zinc-500 text-xs mb-3">Je betaling staat nog open. Klik hieronder om verder te gaan met betalen.</p>
            <a
              href={subscription.checkout_url}
              data-testid="hervatten-betaling-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A84C] text-black font-bold text-sm hover:bg-[#d4b05c] transition-all"
            >
              <CreditCard size={14} />
              Betaling hervatten
              <ArrowRight size={13} />
            </a>
          </div>
        )}

        {hasActive && !subscription.override && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <button
              onClick={cancelSubscription}
              disabled={canceling}
              className="text-red-500/70 hover:text-red-400 text-xs transition-colors disabled:opacity-50"
            >
              {canceling ? "Opzeggen..." : "Abonnement opzeggen"}
            </button>
          </div>
        )}
      </div>

      {/* Pakket kiezen */}
      {!hasActive && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Kies een pakket</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map(p => (
              <div
                key={p.key}
                onClick={() => setSelectedPlan(p.key)}
                data-testid={`plan-card-${p.key}`}
                className={`relative rounded-2xl p-5 cursor-pointer transition-all border ${
                  selectedPlan === p.key
                    ? "border-[#C9A84C] bg-[#0f0d07] shadow-[0_0_20px_rgba(201,168,76,0.1)]"
                    : "border-white/8 bg-[#111] hover:border-white/20"
                }`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#C9A84C] text-black text-xs font-black">
                      <Star size={9} fill="black" />{p.badge}
                    </span>
                  </div>
                )}
                <div className="flex items-start justify-between mb-3">
                  <span className={`text-xs font-black uppercase tracking-widest ${selectedPlan === p.key ? "text-[#C9A84C]" : "text-zinc-500"}`}>
                    {p.naam}
                  </span>
                  {selectedPlan === p.key && <CheckCircle size={14} className="text-[#C9A84C]" />}
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-3xl font-black text-white">€{p.prijs}</span>
                  <span className="text-zinc-500 text-xs">/4w</span>
                </div>
                <ul className="space-y-1.5">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-zinc-400 text-xs">
                      <CheckCircle size={10} className="text-[#C9A84C] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-5 space-y-3">
            <p className="text-zinc-600 text-xs">
              Minimale looptijd 16 weken. Daarna per 4 weken opzegbaar.
              Extra sporters: €2/sporter/4 weken. Betaling via iDEAL (eerste keer), daarna automatische incasso.
            </p>

            {/* Kortingscode */}
            <div>
              <p className="text-zinc-500 text-xs font-semibold mb-1.5">Kortingscode (optioneel)</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={kortingscode}
                  onChange={e => { setKortingscode(e.target.value.toUpperCase()); setKortingPercentage(0); setKortingFout(""); }}
                  placeholder="JOUWCODE"
                  data-testid="abonnement-kortingscode-input"
                  className="flex-1 bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:border-[#C9A84C]/50 uppercase"
                />
                <button
                  type="button"
                  onClick={valideerKorting}
                  disabled={kortingValideren || !kortingscode.trim()}
                  data-testid="abonnement-kortingscode-toepassen-btn"
                  className="px-4 py-2.5 rounded-xl font-bold text-xs border border-white/10 text-zinc-400 hover:border-[#C9A84C]/40 hover:text-[#C9A84C] disabled:opacity-40 transition-all whitespace-nowrap"
                >
                  {kortingValideren ? "..." : "Toepassen"}
                </button>
              </div>
              {kortingPercentage > 0 && (
                <p className="mt-1.5 text-green-400 text-xs font-semibold flex items-center gap-1">
                  <CheckCircle size={12} /> {kortingPercentage}% korting toegepast!
                </p>
              )}
              {kortingFout && <p className="mt-1.5 text-red-400 text-xs">{kortingFout}</p>}
            </div>

            <button
              onClick={() => startPayment(selectedPlan)}
              disabled={initiating}
              data-testid="start-abonnement-btn"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#C9A84C] text-black font-bold text-sm hover:bg-[#d4b05c] disabled:opacity-60 transition-all"
            >
              <CreditCard size={15} />
              {initiating
                ? "Betaling starten..."
                : kortingPercentage > 0
                  ? `Starten — €${Math.round(PLANS.find(p => p.key === selectedPlan)?.prijs * (1 - kortingPercentage / 100))}/4w (${kortingPercentage}% korting)`
                  : `Starten met ${selectedPlan.toUpperCase()} — €${PLANS.find(p => p.key === selectedPlan)?.prijs}/4w`
              }
              <ArrowRight size={14} />
            </button>
            <div className="flex items-center gap-1.5">
              <Zap size={11} className="text-[#C9A84C]" />
              <span className="text-zinc-600 text-xs">Geen opstartkosten · Direct toegang na activatie</span>
            </div>
          </div>
        </div>
      )}

      {/* Extra sporters (self-service) — alleen als actief abonnement */}
      {hasActive && (
        <div className="bg-[#111] border border-white/8 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <Users size={16} className="text-[#C9A84C]" />
            <h3 className="text-white font-semibold">Extra sporters</h3>
          </div>
          <p className="text-zinc-500 text-sm mb-5">
            Voeg extra plekken toe bovenop je pakket. Extra sporters kosten €2,- per sporter per 4 weken.
          </p>

          {/* Capaciteitsoverzicht */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-white/3 border border-white/6 rounded-xl p-3 text-center">
              <p className="text-white font-bold text-xl">{subscription?.sporters_limit || 0}</p>
              <p className="text-zinc-600 text-xs mt-0.5">Pakket limiet</p>
            </div>
            <div className="bg-[#C9A84C]/6 border border-[#C9A84C]/20 rounded-xl p-3 text-center">
              <p className="text-[#C9A84C] font-bold text-xl">+{extraSporters}</p>
              <p className="text-zinc-600 text-xs mt-0.5">Extra sporters</p>
            </div>
            <div className="bg-white/3 border border-white/6 rounded-xl p-3 text-center">
              <p className="text-white font-bold text-xl">{(subscription?.sporters_limit || 0) + extraSporters}</p>
              <p className="text-zinc-600 text-xs mt-0.5">Totale capaciteit</p>
            </div>
          </div>

          {/* +/- besturing */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => setExtraSporters(v => Math.max(0, v - 1))}
              className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center transition-all"
              data-testid="extra-minus-btn"
            >
              <Minus size={16} />
            </button>
            <input
              type="number"
              min="0"
              max="500"
              value={extraSporters}
              onChange={e => setExtraSporters(Math.max(0, parseInt(e.target.value) || 0))}
              className="flex-1 bg-[#0f0f0f] border border-white/10 rounded-xl px-4 py-2.5 text-white text-center text-2xl font-bold focus:outline-none focus:border-[#C9A84C]/50"
              data-testid="extra-sporters-bedrijf-input"
            />
            <button
              onClick={() => setExtraSporters(v => v + 1)}
              className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center transition-all"
              data-testid="extra-plus-btn"
            >
              <Plus size={16} />
            </button>
          </div>

          {/* Kostenoverzicht */}
          {extraSporters > 0 && (
            <div className="bg-[#C9A84C]/6 border border-[#C9A84C]/15 rounded-xl px-4 py-3 mb-4 flex items-center justify-between text-sm">
              <span className="text-zinc-400">{extraSporters} extra sporter{extraSporters !== 1 ? "s" : ""}</span>
              <span className="text-[#C9A84C] font-bold">+€{(extraSporters * 2).toFixed(2)}/4 weken</span>
            </div>
          )}

          {/* Feedback */}
          {extraResult && (
            <div className={`mb-4 p-3 rounded-xl text-sm flex items-center gap-2 ${extraResult.ok ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
              {extraResult.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
              {extraResult.msg}
            </div>
          )}

          <button
            onClick={handleSaveExtraSporters}
            disabled={extraSaving || extraSporters === (subscription?.extra_sporters || 0)}
            data-testid="save-extra-sporters-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A84C] text-black font-bold text-sm hover:bg-[#d4b05c] disabled:opacity-40 transition-all"
          >
            {extraSaving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            {extraSaving ? "Opslaan..." : "Capaciteit opslaan"}
          </button>
        </div>
      )}

      {/* Upgraden */}
      {hasActive && (
        <div className="bg-[#111] border border-white/8 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">Pakket upgraden?</h3>
              <p className="text-zinc-500 text-sm mt-0.5">Neem contact op via info@fitsmith.nl</p>
            </div>
            <Shield size={20} className="text-zinc-600" />
          </div>
        </div>
      )}
    </div>
  );
}
